import React, { useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CalendarCheck, Clock, Users, AlertCircle } from 'lucide-react';
import { StatCard } from '@/components/dashboard/StatCard';
import { AccreditationProgress } from '@/components/dashboard/AccreditationProgress';
import { SupervisionSummary } from '@/components/supervision/SupervisionSummary';
import { CPDSummary } from '@/components/dashboard/CPDSummary';
import { DashboardHeader } from '@/components/dashboard/DashboardHeader';
import { useDashboardStats } from '@/hooks/useDashboardStats';
import DashboardErrorBoundary from '@/components/dashboard/ErrorBoundary';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';

const Dashboard = () => {
  const { stats, loading, error } = useDashboardStats();

  useEffect(() => {
    // Initialize listener for auth changes to help debug issues
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('Auth state changed in Dashboard:', event, 'session:', !!session);
    });
    // Check auth status on mount
    supabase.auth.getSession().then(({ data }) => {
      console.log('Dashboard auth check:', data.session ? 'Session active' : 'No session');
    });
    return () => {
      if (authListener?.subscription) authListener.subscription.unsubscribe();
    };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coaching-primary mx-auto mb-4"></div>
          <p>Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="w-full mx-auto my-12 max-w-md card">
        <CardHeader className="bg-red-50">
          <CardTitle className="text-red-700 flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            Error Loading Dashboard
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <p className="text-sm text-gray-700 mb-4">
            There was an error loading your dashboard data. This might be due to an authentication issue.
          </p>
          <p className="text-xs text-gray-500 mb-2">
            Error: {error.message}
          </p>
          <div className="mt-4 flex justify-between">
            <Button 
              variant="outline" 
              onClick={() => window.location.reload()}
            >
              Retry
            </Button>
            <Button 
              variant="default"
              onClick={async () => {
                try {
                  await supabase.auth.refreshSession();
                  window.location.reload();
                } catch (e) {
                  console.error(e);
                }
              }}
            >
              Refresh Session
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!stats) {
    return (
      <div className="flex items-center justify-center h-96">
        <p>No data available. Please add clients and sessions first.</p>
      </div>
    );
  }

  return (
    <DashboardErrorBoundary>
      <div className="space-y-6 min-h-screen p-4 md:p-8" style={{ background: 'linear-gradient(120deg, var(--color-bg-main) 60%, var(--color-bg-alt) 100%)' }}>
        <DashboardHeader />
        
        {/* Stats Overview */}
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
          <StatCard
            title="Total Clients"
            value={stats.totalClients.toString()}
            icon={<Users className="h-5 w-5 icon-accent" />}
            description="Number of clients"
          />
          <StatCard
            title="Total Sessions"
            value={stats.totalSessions.toString()}
            icon={<CalendarCheck className="h-5 w-5 icon-accent" />}
            description="Sessions conducted"
          />
          <StatCard
            title="Coaching Hours"
            value={stats.totalHours.toFixed(1)}
            icon={<Clock className="h-5 w-5 icon-accent" />}
            description="Total hours logged"
          />
        </div>
        
        {/* Main Dashboard Content - Full Width */}
        <div className="space-y-6">
          {/* Accreditation Progress */}
          <AccreditationProgress totalHours={stats?.totalHours || 0} />
          
          {/* Supervision and CPD Summary */}
          <div className="grid gap-6 grid-cols-1 md:grid-cols-2">
            <SupervisionSummary 
              totalHours={stats.totalSupervisionHours} 
              supervisionCount={stats.totalSupervisionSessions}
            />
            <CPDSummary />
          </div>
          
          {/* This Month Activity */}
          <Card className="card">
            <CardHeader>
              <CardTitle className="text-lg font-medium">This Month</CardTitle>
              <CardDescription>Activity for the current month</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-8 md:gap-16 max-w-md mx-auto">
                <div className="space-y-2 text-center">
                  <p className="text-sm text-muted-foreground">Sessions</p>
                  <p className="text-3xl font-bold">{stats.sessionsThisMonth}</p>
                </div>
                <div className="space-y-2 text-center">
                  <p className="text-sm text-muted-foreground">Hours</p>
                  <p className="text-3xl font-bold">{stats.hoursThisMonth.toFixed(1)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardErrorBoundary>
  );
};

export default Dashboard;
